import type React from 'react';
import type { ChangeEvent, ReactNode, RefObject } from 'react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import type { ColorName } from 'styles/config/colors';

import ConditionalWrapper from '@polyai/common/utils/conditionalWrapper';
import { safeResizeObserver } from '@polyai/common/utils/safeResizeObserver';

import type { BaseIcon } from '../Icons/BaseIcon';
import type { InputWrapperProps } from '../InputWrapper';
import InputWrapper from '../InputWrapper';
import * as Styled from './Input.styled';

export { TextArea } from './Input.styled';

export type InputProps = React.InputHTMLAttributes<
  HTMLInputElement | HTMLTextAreaElement
> & {
  label?: ReactNode;
  labelColor?: ColorName;
  errors?: string[];
  rows?: number;
  loading?: boolean;
  autoExpand?: boolean;
  fullWidth?: boolean;
  showErrorAsTooltip?: boolean;
  showCharacterCount?: boolean;
  characterCountPosition?: 'top' | 'bottom';
  Icon?: typeof BaseIcon;
  hint?: ReactNode;
  textArea?: boolean;
  isKeyboardSafe?: boolean;
  readonlySuffix?: string;
  'data-test-id-tooltip'?: string;
  'data-1p-ignore'?: boolean;
  inputContainerClassName?: string;
} & InputWrapperProps;

export const Input = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  InputProps
>(
  (
    {
      label,
      labelColor,
      errors = [],
      rows = 1,
      maxLength,
      className,
      loading,
      readonlySuffix,
      autoExpand,
      fullWidth,
      showErrorAsTooltip,
      showCharacterCount,
      characterCountPosition = 'bottom',
      defaultValue,
      onChange,
      Icon,
      hint,
      textArea,
      help,
      rightLabel,
      'data-dd-action': ddActionName,
      isKeyboardSafe = false,
      portal = false,
      inverse,
      'data-test-id-tooltip': tooltipTestId = 'tooltip-text',
      'data-1p-ignore': data1pIgnore = true,
      inputContainerClassName,
      children,
      ...props
    },
    ref,
  ) => {
    const [length, setLength] = useState(defaultValue?.toString().length || 0);
    const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

    const [hintWidth, setHintWidth] = useState<number>();
    const hintRef = useRef<HTMLDivElement | null>(null);
    const [suffixWidth, setSuffixWidth] = useState<number>();
    const suffixRef = useRef<HTMLSpanElement | null>(null);

    const updateLength = useCallback(
      (value?: typeof defaultValue) => {
        setLength(value?.toString()?.length || 0);

        if (
          !CSS.supports('field-sizing', 'content') &&
          textAreaRef.current &&
          autoExpand
        ) {
          const currentTextRef = textAreaRef.current;

          // store the page scroll position to return to this once
          // text area is resized
          const pageScrollTop = document.documentElement.scrollTop;

          currentTextRef.style.height = '0px';

          const textAreaScrollHeight = currentTextRef.scrollHeight;
          currentTextRef.style.height = `${textAreaScrollHeight}px`;

          window.scrollTo(0, pageScrollTop);
        }
      },
      [autoExpand],
    );

    useEffect(() => {
      if (textAreaRef.current) {
        updateLength(
          props.value !== textAreaRef.current.value
            ? props.value
            : textAreaRef.current.value,
        );
      } else {
        updateLength(props.value);
      }
    }, [updateLength, props.value]);

    // HACK fix to make sure we capture the latest scrollHeight on first load
    useLayoutEffect(() => {
      if (
        !CSS.supports('field-sizing', 'content') &&
        textAreaRef.current &&
        autoExpand
      ) {
        const setSize = () => {
          const currentTextRef = textAreaRef.current;

          if (!currentTextRef) {
            return;
          }

          setTimeout(() => {
            const scrollHeight = currentTextRef.scrollHeight;
            currentTextRef.style.height = `${scrollHeight}px`;
          }, 100);
        };

        const sizeObserver = safeResizeObserver(() => {
          setSize();
        });

        setSize();
        sizeObserver.observe(textAreaRef.current);

        return () => {
          textAreaRef.current && sizeObserver.unobserve(textAreaRef.current);
        };
      }
    }, [autoExpand]);

    useLayoutEffect(() => {
      if (hintRef.current && !props.hidden) {
        setHintWidth(hintRef.current?.offsetWidth);
      }
      if (suffixRef.current && !props.hidden) {
        setSuffixWidth(suffixRef.current?.offsetWidth);
      }
    }, [props.hidden]);

    const characterCountNode =
      showCharacterCount && typeof maxLength === 'number' ? (
        <Styled.MaxLengthCaption $withError={length > maxLength}>
          {length}/{maxLength}
        </Styled.MaxLengthCaption>
      ) : null;

    return (
      <InputWrapper
        afterErrors={
          characterCountPosition === 'bottom' ? characterCountNode : undefined
        }
        data-test-id-tooltip={tooltipTestId}
        errors={errors}
        fullWidth={fullWidth}
        help={help}
        inverse={inverse}
        label={label}
        labelColor={labelColor}
        portal={portal}
        rightLabel={
          characterCountPosition === 'top' && !rightLabel
            ? characterCountNode
            : rightLabel
        }
        showErrorAsTooltip={showErrorAsTooltip}
      >
        {rows > 1 || textArea ? (
          <Styled.TextArea
            ref={input => {
              textAreaRef.current = input;
              if (typeof ref === 'function') {
                ref(input);
              } else if (ref) {
                ref.current = input;
              }
            }}
            $autoExpand={autoExpand}
            $inverse={inverse}
            $withError={errors?.length > 0}
            className={className}
            data-dd-action={ddActionName}
            defaultValue={defaultValue}
            isKeyboardSafe={isKeyboardSafe}
            maxLength={maxLength}
            readOnly={props.readOnly}
            rows={rows}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
              updateLength(e.target.value);
              if (onChange) {
                onChange(e);
              }
            }}
            {...(data1pIgnore ? { 'data-1p-ignore': true } : {})}
            {...props}
          />
        ) : (
          <ConditionalWrapper
            condition={!!autoExpand}
            wrapper={children => (
              <Styled.AutoExpandInputWrapper
                className={className}
                data-value={
                  props.value ? `${props.value}-` : `${props.placeholder}-`
                }
              >
                {children}
              </Styled.AutoExpandInputWrapper>
            )}
          >
            <Styled.InputContainer
              $fullWidth={fullWidth}
              className={inputContainerClassName}
            >
              <Styled.Input
                ref={ref as RefObject<HTMLInputElement>}
                $autoExpand={autoExpand}
                $fullWidth={fullWidth}
                $hasIcon={!!Icon}
                $hintWidth={hintWidth}
                $inverse={inverse}
                $suffixWidth={suffixWidth}
                $withError={errors?.length > 0}
                aria-readonly={props.readOnly}
                className={className}
                data-dd-action={ddActionName}
                defaultValue={defaultValue}
                isKeyboardSafe={isKeyboardSafe}
                maxLength={maxLength}
                readOnly={props.readOnly}
                style={Icon ? { paddingLeft: 40 } : undefined}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setLength(e.target.value.length);
                  if (onChange) {
                    onChange(e);
                  }
                }}
                {...(data1pIgnore ? { 'data-1p-ignore': true } : {})}
                {...props}
              />
              {Icon && (
                <Styled.IconWrapper>
                  <Icon />
                </Styled.IconWrapper>
              )}
              {loading && <Styled.LoadingSpinner size={16} strokeWidth={1} />}
              {hint && (
                <Styled.HintWrapper ref={hintRef}>{hint}</Styled.HintWrapper>
              )}
              {readonlySuffix && (
                <Styled.ReadonlySuffix ref={suffixRef} aria-hidden="true">
                  {readonlySuffix}
                </Styled.ReadonlySuffix>
              )}
              {children}
            </Styled.InputContainer>
          </ConditionalWrapper>
        )}
      </InputWrapper>
    );
  },
);

export default Input;
